import { createTheme } from '@mui/material/styles';
import {
    figmaVariables,
    lightColors,
    darkColors,
    typography,
    spacing,
    shadows,
    borderRadius,
    transitions,
} from './figmaVariables';

// Light Theme
export const lightTheme = createTheme({
    palette: {
        mode: 'light',

        // Primary Colors
        primary: {
            main: lightColors.primary.main,
            50: lightColors.primary[50],
            100: lightColors.primary[100],
            200: lightColors.primary[200],
            300: lightColors.primary[300],
            400: lightColors.primary[400],
            500: lightColors.primary[500],
            600: lightColors.primary[600],
            700: lightColors.primary[700],
            800: lightColors.primary[800],
            900: lightColors.primary[900],
            light: lightColors.primary[100],
            dark: lightColors.primary[800],
            contrastText: lightColors.neutral.white,
        },

        // Secondary Colors
        secondary: {
            main: lightColors.secondary.main,
            50: lightColors.secondary[50],
            100: lightColors.secondary[100],
            200: lightColors.secondary[200],
            300: lightColors.secondary[300],
            400: lightColors.secondary[400],
            500: lightColors.secondary[500],
            600: lightColors.secondary[600],
            700: lightColors.secondary[700],
            800: lightColors.secondary[800],
            900: lightColors.secondary[900],
            light: lightColors.secondary[100],
            dark: lightColors.secondary[800],
            contrastText: lightColors.neutral.white,
        },

        // Semantic Colors
        success: {
            main: lightColors.semantic.success.main,
            light: lightColors.semantic.success.light,
            dark: lightColors.semantic.success.dark,
            contrastText: lightColors.neutral.white,
        },
        warning: {
            main: lightColors.semantic.warning.main,
            light: lightColors.semantic.warning.light,
            dark: lightColors.semantic.warning.dark,
            contrastText: lightColors.neutral.white,
        },
        error: {
            main: lightColors.semantic.error.main,
            light: lightColors.semantic.error.light,
            dark: lightColors.semantic.error.dark,
            contrastText: lightColors.neutral.white,
        },
        info: {
            main: lightColors.semantic.info.main,
            light: lightColors.semantic.info.light,
            dark: lightColors.semantic.info.dark,
            contrastText: lightColors.neutral.white,
        },

        // Background Colors
        background: {
            default: lightColors.background.default,
            paper: lightColors.background.sub,
            surface: lightColors.background.surface,
            main: lightColors.background.main,
            sub: lightColors.background.sub,
        },

        // Text Colors
        text: {
            primary: lightColors.text.primary,
            secondary: lightColors.text.secondary,
            disabled: lightColors.text.disabled,
            inverse: lightColors.text.inverse,
            main: lightColors.text.main,
        },

        // Neutral Colors
        neutral: {
            white: lightColors.neutral.white,
            black: lightColors.neutral.black,
            gray: lightColors.neutral.gray,
        },

        // Figma Brand Colors
        figma: {
            white: lightColors.figma.white,
            radialGradient: {
                primary: lightColors.primary.main,
                secondary: lightColors.secondary.main,
            },
            // New Figma Colors
            black: lightColors.figma.black,
            icon: lightColors.figma.icon,
            mainBg: lightColors.figma.mainBg,
            Red_didim: lightColors.figma.Red_didim,
            buttonText: lightColors.figma.buttonText,
            iconWhite: lightColors.figma.iconWhite,
            buttonOrange: lightColors.figma.buttonOrange,
            block: lightColors.figma.block,
            bottomColor: lightColors.figma.bottomColor,
            steps: lightColors.figma.steps,
            stepsDark: lightColors.figma.stepsDark,
            didim: lightColors.figma.didim,
            calorieRed: lightColors.figma.calorieRed,
            time: lightColors.figma.time,
            upRed: lightColors.figma.upRed,
            downBlue: lightColors.figma.downBlue,
            heartRate: lightColors.figma.heartRate,
            heartRateDark: lightColors.figma.heartRateDark,
            walkingChallengeBg: lightColors.figma.walkingChallengeBg,
            heartRateChallengeBg: lightColors.figma.heartRateChallengeBg,
            champTournament: lightColors.figma.champTournament,
            champTournamentFinish: lightColors.figma.champTournamentFinish,
            champTournamentHighlight: lightColors.figma.champTournamentHighlight,
            yellowText: lightColors.figma.yellowText,
            champRanking: lightColors.figma.champRanking,
            champRankingFinish: lightColors.figma.champRankingFinish,
            champRankingHighlight: lightColors.figma.champRankingHighlight,
            popupBG: lightColors.figma.popupBG,
        },

        // Legacy Colors (for backward compatibility)
        pink: {
            light: '#ffebee',
            main: '#ff4081',
            dark: '#c51162',
        },

        // Figma shadows (elevation 0~5, custom.card/button/modal/tooltip)
        shadows,
    },

    typography: {
        fontFamily: typography.fontFamily.primary,

        // Figma Typography Variants
        digitalPlayground: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.digitalPlayground.fontSize,
            fontWeight: typography.variants.digitalPlayground.fontWeight,
            letterSpacing: typography.variants.digitalPlayground.letterSpacing,
            lineHeight: typography.variants.digitalPlayground.lineHeight,
        },
        didimLogo: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.didimLogo.fontSize,
            fontWeight: typography.variants.didimLogo.fontWeight,
            letterSpacing: typography.variants.didimLogo.letterSpacing,
            lineHeight: typography.variants.didimLogo.lineHeight,
        },

        // Component Typography
        h1: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.h1.fontSize,
            fontWeight: typography.variants.h1.fontWeight,
            lineHeight: typography.variants.h1.lineHeight,
        },
        h2: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.h2.fontSize,
            fontWeight: typography.variants.h2.fontWeight,
            lineHeight: typography.variants.h2.lineHeight,
        },
        h3: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.h3.fontSize,
            fontWeight: typography.variants.h3.fontWeight,
            lineHeight: typography.variants.h3.lineHeight,
        },
        h4: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.h4.fontSize,
            fontWeight: typography.variants.h4.fontWeight,
            lineHeight: typography.variants.h4.lineHeight,
        },
        h5: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.h5.fontSize,
            fontWeight: typography.variants.h5.fontWeight,
            lineHeight: typography.variants.h5.lineHeight,
        },
        h6: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.h6.fontSize,
            fontWeight: typography.variants.h6.fontWeight,
            lineHeight: typography.variants.h6.lineHeight,
        },
        body1: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.body1.fontSize,
            fontWeight: typography.variants.body1.fontWeight,
            lineHeight: typography.variants.body1.lineHeight,
        },
        body2: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.body2.fontSize,
            fontWeight: typography.variants.body2.fontWeight,
            lineHeight: typography.variants.body2.lineHeight,
        },
        caption: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.caption.fontSize,
            fontWeight: typography.variants.caption.fontWeight,
            lineHeight: typography.variants.caption.lineHeight,
        },
        button: {
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.variants.button.fontSize,
            fontWeight: typography.variants.button.fontWeight,
            lineHeight: typography.variants.button.lineHeight,
            textTransform: typography.variants.button.textTransform,
        },
    },

    // Spacing
    spacing: factor => spacing[factor] || `${factor * 4}px`,

    // Shadows
    shadows: [
        'none',
        ...Object.values(shadows.elevation),
        // Material-UI default shadows for higher elevations
        '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
        '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
        '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
        '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
        '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
        '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
        '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
        '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
        '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
        '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
        '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
        '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
        '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
        '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
        '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
        '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
        '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
        ...Object.values(shadows.custom),
    ],

    // Border Radius
    shape: {
        borderRadius: borderRadius.base,
    },

    // Transitions
    transitions: {
        duration: transitions.duration,
        easing: transitions.easing,
    },

    // Figma Variables for custom components
    figma: {
        spacing,
        sizes: figmaVariables.sizes,
        borderRadius,
        zIndex: figmaVariables.zIndex,
        breakpoints: figmaVariables.breakpoints,
        transitions,
    },

    // Component overrides
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
            defaultProps: {
                color: 'text.main',
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius.md,
                    textTransform: 'none',
                    fontWeight: typography.fontWeight.medium,
                },
                sizeSmall: {
                    height: figmaVariables.sizes.button.small.height,
                    padding: figmaVariables.sizes.button.small.padding,
                },
                sizeMedium: {
                    height: figmaVariables.sizes.button.medium.height,
                    padding: figmaVariables.sizes.button.medium.padding,
                },
                sizeLarge: {
                    height: figmaVariables.sizes.button.large.height,
                    padding: figmaVariables.sizes.button.large.padding,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: borderRadius.md,
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius.lg,
                    boxShadow: shadows.custom.card,
                    '--Paper-overlay': 'transparent !important',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    '--Paper-overlay': 'transparent !important',
                },
            },
        },
        // Table Components Default Styling
        MuiTableContainer: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.figma.white[10],
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.figma.white[20]}`,
                    overflow: 'hidden',
                }),
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    minWidth: 650,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiTableRow-root': {
                        backgroundColor: theme.palette.figma.white[40],
                    },
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontWeight: 600,
                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                    borderBottom: `1px solid ${theme.palette.figma.white[30]}`,
                }),
                head: ({ theme }) => ({
                    fontWeight: 600,
                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                    borderBottom: `1px solid ${theme.palette.figma.white[30]}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        backgroundColor: theme.palette.figma.white[20],
                    },
                }),
                body: ({ theme }) => ({
                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                    borderBottom: `1px solid ${theme.palette.figma.white[20]}`,
                }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.figma.white[5]
                    },
                    '&:hover': {
                        backgroundColor: theme.palette.figma.white[20]
                    },
                    borderBottom: `1px solid ${theme.palette.figma.white[20]}`,
                }),
            },
        },
    },
});

// Dark Theme
export const darkTheme = createTheme({
    ...lightTheme,
    palette: {
        mode: 'dark',

        // Primary Colors (same as light for brand consistency)
        primary: lightTheme.palette.primary,
        secondary: lightTheme.palette.secondary,

        // Semantic Colors (adjusted for dark mode)
        success: {
            main: darkColors.semantic.success.main,
            light: darkColors.semantic.success.light,
            dark: darkColors.semantic.success.dark,
            contrastText: darkColors.neutral.white,
        },
        warning: {
            main: darkColors.semantic.warning.main,
            light: darkColors.semantic.warning.light,
            dark: darkColors.semantic.warning.dark,
            contrastText: darkColors.neutral.white,
        },
        error: {
            main: darkColors.semantic.error.main,
            light: darkColors.semantic.error.light,
            dark: darkColors.semantic.error.dark,
            contrastText: darkColors.neutral.white,
        },
        info: {
            main: darkColors.semantic.info.main,
            light: darkColors.semantic.info.light,
            dark: darkColors.semantic.info.dark,
            contrastText: darkColors.neutral.white,
        },

        // Background Colors
        background: {
            default: darkColors.background.default,
            paper: darkColors.background.surface,
            surface: darkColors.background.surface,
            main: darkColors.background.main,
            sub: darkColors.background.sub,
        },

        // Text Colors
        text: {
            primary: darkColors.text.primary,
            secondary: darkColors.text.secondary,
            disabled: darkColors.text.disabled,
            inverse: darkColors.text.inverse,
            main: darkColors.text.main,
        },

        // Neutral Colors
        neutral: {
            white: darkColors.neutral.white,
            black: darkColors.neutral.black,
            gray: darkColors.neutral.gray,
        },

        // Figma Brand Colors (same for brand consistency)
        figma: {
            white: darkColors.figma.white,
            radialGradient: lightTheme.palette.figma.radialGradient,
            // New Figma Colors for Dark Mode
            black: darkColors.figma.black,
            icon: darkColors.figma.icon,
            mainBg: darkColors.figma.mainBg,
            Red_didim: darkColors.figma.Red_didim,
            buttonText: darkColors.figma.buttonText,
            iconWhite: darkColors.figma.iconWhite,
            buttonOrange: darkColors.figma.buttonOrange,
            block: darkColors.figma.block,
            bottomColor: darkColors.figma.bottomColor,
            steps: darkColors.figma.steps,
            stepsDark: darkColors.figma.stepsDark,
            didim: darkColors.figma.didim,
            calorieRed: darkColors.figma.calorieRed,
            time: darkColors.figma.time,
            upRed: darkColors.figma.upRed,
            downBlue: darkColors.figma.downBlue,
            heartRate: darkColors.figma.heartRate,
            heartRateDark: darkColors.figma.heartRateDark,
            walkingChallengeBg: darkColors.figma.walkingChallengeBg,
            heartRateChallengeBg: darkColors.figma.heartRateChallengeBg,
            champTournament: darkColors.figma.champTournament,
            champTournamentFinish: darkColors.figma.champTournamentFinish,
            champTournamentHighlight: darkColors.figma.champTournamentHighlight,
            yellowText: darkColors.figma.yellowText,
            champRanking: darkColors.figma.champRanking,
            champRankingFinish: darkColors.figma.champRankingFinish,
            champRankingHighlight: darkColors.figma.champRankingHighlight,
            popupBG: darkColors.figma.popupBG,
        },
    },

    // Component overrides for dark theme
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
            defaultProps: {
                color: 'text.main',
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius.lg,
                    boxShadow: shadows.custom.card,
                    '--Paper-overlay': 'transparent !important',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    '--Paper-overlay': 'transparent !important',
                },
            },
        },
        // Table Components Default Styling (inherited from light theme)
        MuiTableContainer: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.figma.white[10],
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.figma.white[20]}`,
                    overflow: 'hidden',
                }),
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    minWidth: 650,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiTableRow-root': {
                        backgroundColor: theme.palette.figma.white[40],
                    },
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontWeight: 600,
                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                    borderBottom: `1px solid ${theme.palette.figma.white[30]}`,
                }),
                head: ({ theme }) => ({
                    fontWeight: 600,
                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                    borderBottom: `1px solid ${theme.palette.figma.white[30]}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        backgroundColor: theme.palette.figma.white[20],
                    },
                }),
                body: ({ theme }) => ({
                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                    borderBottom: `1px solid ${theme.palette.figma.white[20]}`,
                }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.figma.white[5]
                    },
                    '&:hover': {
                        backgroundColor: theme.palette.figma.white[20]
                    },
                    borderBottom: `1px solid ${theme.palette.figma.white[20]}`,
                }),
            },
        },
    },
});

// Default export (light theme)
export default lightTheme;

/** 유저 앱 vw/vh(360×780 기준) — 예: import { pxW } from './theme'; */
export {
    pxW,
    pxH,
    DESIGN_BASE_WIDTH,
    DESIGN_BASE_HEIGHT,
} from './designViewportUnits';
